import { makeAutoObservable, observable } from "mobx";
import type { RowSelectionState, SortingState } from "@tanstack/react-table";
import type { SkillsPageQuery } from "@/queries/skills";

export type SkillStatusFilter = "active" | "archived";

/**
 * The settings Skills table's UI state: its filters, sort and row selection.
 * The rows are the cached `skills.page` pages (`useSkillsPage`, NG-2805), read
 * under `pageQuery`; the full list every other reader needs is `skills.bank`
 * (`useSkillBank`, NG-2802).
 */
export class WorkspaceSkillsStore {
  // Filter state for the management list. Pushed in by the route on every
  // column-filter change; `pageQuery` reads these.
  searchQuery = "";
  userIdsFilter = observable.set<string>();
  statusFilter = observable.set<SkillStatusFilter>();
  // Single-column sort for the management list (server-side via `order_by`).
  sorting: SortingState = [];

  // Row-selection state for the management list (drives CSV-of-selection).
  rowSelection: RowSelectionState = {};

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** The table's server query: the filters and sort the pages and the CSV
   *  export are read under. A new object only when one of them changes. */
  get pageQuery(): SkillsPageQuery {
    const query: SkillsPageQuery = {};
    const trimmed = this.searchQuery.trim();
    if (trimmed) query.q = trimmed;
    if (this.userIdsFilter.size > 0) query.user_ids = [...this.userIdsFilter];
    // Status filter is a multi-select; only forward `archived` to the BE
    // when exactly one is selected — selecting both is equivalent to "no
    // filter", and the BE has no tri-state filter on a single param.
    if (this.statusFilter.size === 1) {
      const [only] = [...this.statusFilter];
      query.archived = only === "archived";
    }
    const sort = this.sorting[0];
    if (sort) query.order_by = `${sort.desc ? "-" : ""}${sort.id}`;
    return query;
  }

  // ---- Filter setters ---------------------------------------------------

  setSearchQuery(query: string): void {
    this.searchQuery = query;
  }

  setUserIdsFilter(ids: Iterable<string>): void {
    this.userIdsFilter.replace([...ids]);
  }

  setStatusFilter(values: Iterable<SkillStatusFilter>): void {
    this.statusFilter.replace([...values]);
  }

  /** Set the single-column sort; the table reads its first page under it. */
  setSorting(next: SortingState): void {
    this.sorting = next;
  }

  setRowSelection(next: RowSelectionState): void {
    this.rowSelection = next;
  }

  clearListFilters(): void {
    this.searchQuery = "";
    this.userIdsFilter.clear();
    this.statusFilter.clear();
    this.sorting = [];
  }
}
